/* Blok belgesinin üzerindeki işlemler — editörün değiştirdiği her şey buradan.

   İşlevler belgeyi **yerinde** değiştiriyor; çağıran (views/design.ts) önce
   bir kopya alıyor, işlemi uyguluyor, sonucu şemadan geçiriyor ve ancak
   geçerse kabul ediyor. Geçmiş (geri al) o kopyalardan oluşuyor.

   Kimlikler belge içinde tekil (blocks.ts `pageSchema`). Çoğaltılan ya da
   eklenen her blok ve öğe yeni kimlik alıyor (`freshIds`). */

import { allIds, childLists, newId, type Block, type ListItem, type Rich } from '@frawgraphy/shared';

export type Loc = { list: Block[]; index: number; block: Block; parent: Block | null };

/** Bir bloğun yeri: içinde durduğu liste, sırası ve kabı. */
export function locate(blocks: Block[], id: string, parent: Block | null = null): Loc | null {
  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i]!;
    if (block.id === id) return { list: blocks, index: i, block, parent };
    for (const list of childLists(block)) {
      const hit = locate(list, id, block);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Bölüm, hero, menü, footer ve sayfa bölgesi yalnızca en üstte; diğer
 * bloklar yalnızca bir kabın içinde.
 */
export function isTopLevel(block: Block): boolean {
  return block.type === 'section' || block.type === 'hero' || block.type === 'nav' || block.type === 'footer' || block.type === 'page';
}

/* ── Alan yolları ───────────────────────────────────────── */

/** `items.2.title` gibi bir yolu nesne üzerinde yürür. */
export function getPath(root: unknown, path: string): unknown {
  let cursor: unknown = root;
  for (const part of path.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

/**
 * Bir metin alanını yazar. EN boş bırakılırsa silinir (site TR'yi gösterir);
 * TR boş bırakılabilir — yönetici bir metni tamamen kaldırmak istediğinde
 * bloğu siler, boş bir başlık da geçerli bir ara hâl.
 */
export function setRich(block: Block, path: string, lang: 'tr' | 'en', html: string): void {
  const parts = path.split('.');
  const last = parts.pop()!;
  const owner = parts.length ? getPath(block, parts.join('.')) : block;
  if (!owner || typeof owner !== 'object') return;
  const target = owner as Record<string, unknown>;
  const current = (target[last] as Rich | undefined) ?? { tr: '' };
  const next: Rich = { ...current };
  if (lang === 'tr') next.tr = html;
  else if (html) next.en = html;
  else delete next.en;
  target[last] = next;
}

/* ── Kimlikler ──────────────────────────────────────────── */

/** Bir bloğun ve içindeki her şeyin kimliğini belgede kullanılmayanlarla değiştirir. */
export function freshIds(block: Block, taken: Set<string>): Block {
  const copy = structuredClone(block);
  const renew = (obj: { id: string; key?: string | undefined }, prefix: string) => {
    obj.id = newId(prefix, taken);
    taken.add(obj.id);
    /* Eski i18n anahtarı kopyaya geçmesin: iki blok aynı sözlük girdisini
       paylaşırsa birinin EN'i diğerini ezerdi. */
    delete obj.key;
  };
  const visit = (b: Block) => {
    renew(b, b.type);
    if (b.type === 'cards' || b.type === 'reels' || b.type === 'facts' || b.type === 'gallery') b.items.forEach((i) => renew(i, 'item'));
    if (b.type === 'contact') b.info.forEach((i) => renew(i, 'info'));
    if (b.type === 'buttons') b.items.forEach((i) => renew(i, 'btn'));
    if (b.type === 'hero') {
      b.lines.forEach((l) => renew(l, 'line'));
      b.buttons.forEach((x) => renew(x, 'btn'));
    }
    if (b.type === 'cta') renew(b.button, 'btn');
    if (b.type === 'coverflow' && b.more) renew(b.more, 'btn');
    if (b.type === 'columns') b.columns.forEach((c) => renew(c, 'col'));
    if (b.type === 'section') b.anchor = newId(b.anchor.replace(/-\d+$/, ''), taken);
    for (const list of childLists(b)) list.forEach(visit);
  };
  visit(copy);
  return copy;
}

export const takenIds = (home: Block[]) => new Set(allIds(home));

/* ── Blok işlemleri ─────────────────────────────────────── */

export function removeBlock(home: Block[], id: string): Loc | null {
  const loc = locate(home, id);
  if (!loc) return null;
  loc.list.splice(loc.index, 1);
  return loc;
}

export function duplicateBlock(home: Block[], id: string): string | null {
  const loc = locate(home, id);
  if (!loc) return null;
  const copy = freshIds(loc.block, takenIds(home));
  loc.list.splice(loc.index + 1, 0, copy);
  return copy.id;
}

export function moveBlock(home: Block[], id: string, step: -1 | 1): boolean {
  const loc = locate(home, id);
  if (!loc) return false;
  const to = loc.index + step;
  if (to < 0 || to >= loc.list.length) return false;
  const [item] = loc.list.splice(loc.index, 1);
  loc.list.splice(to, 0, item!);
  return true;
}

/** `id`'yi `target`'ın önüne ya da arkasına taşır (sürükle-bırak). */
export function moveNextTo(home: Block[], id: string, target: string, after: boolean): boolean {
  if (id === target) return false;
  const from = locate(home, id);
  const to = locate(home, target);
  if (!from || !to) return false;
  /* Bir bloğu kendi içine taşımak ağacı koparırdı. */
  if (locate(childLists(from.block).flat(), target)) return false;
  if (isTopLevel(from.block) !== (to.list === home)) return false;
  const [item] = from.list.splice(from.index, 1);
  const again = locate(home, target)!;
  again.list.splice(again.index + (after ? 1 : 0), 0, item!);
  return true;
}

/**
 * `id`'yi bir sütunun sonuna taşır — boş sütuna bırakmak için (içinde
 * hedef alınacak blok yok). Bölüm gibi en üst düzey bloklar sütuna girmez.
 */
export function moveIntoColumn(root: Block[], id: string, columnId: string): boolean {
  const from = locate(root, id);
  if (!from || isTopLevel(from.block)) return false;
  let column: { id: string; blocks: Block[] } | undefined;
  const search = (list: Block[]) => {
    for (const b of list) {
      if (b.type === 'columns') column ??= b.columns.find((c) => c.id === columnId);
      for (const kids of childLists(b)) search(kids);
    }
  };
  search(root);
  if (!column) return false;
  /* Bir sütunlar bloğunu kendi sütununa taşımak ağacı koparırdı. */
  if (from.block.type === 'columns' && from.block.columns.some((c) => c === column || locate(c.blocks, columnId))) return false;
  from.list.splice(from.index, 1);
  column.blocks.push(from.block);
  return true;
}

/** Yeni bloğu `afterId`'nin arkasına ya da `into` kabının sonuna ekler. */
export function insertBlock(home: Block[], block: Block, where: { afterId?: string; into?: Block[] }): boolean {
  if (where.afterId) {
    const loc = locate(home, where.afterId);
    if (!loc) return false;
    if (isTopLevel(block) !== (loc.list === home)) {
      /* Bölüm bir paragrafın ardına istendiyse: o paragrafın bölümünün ardına. */
      if (isTopLevel(block)) {
        const top = home.findIndex((b) => locate([b], where.afterId!));
        home.splice(top + 1, 0, block);
        return true;
      }
      /* Paragraf bir bölümün ya da sayfa bölgesinin ardına istendiyse: içine, sonuna. */
      if (loc.block.type === 'section' || loc.block.type === 'page') {
        loc.block.blocks.push(block);
        return true;
      }
      return false;
    }
    loc.list.splice(loc.index + 1, 0, block);
    return true;
  }
  (where.into ?? home).push(block);
  return true;
}

/* ── Koleksiyon öğeleri ─────────────────────────────────── */

type ItemList = { id: string }[];

/** Bloğun öğe listesi: kartlar, video kartları, bilgi satırları… */
export function itemsOf(block: Block): ItemList | null {
  switch (block.type) {
    case 'cards':
    case 'reels':
    case 'facts':
    case 'gallery':
    case 'buttons':
      return block.items;
    case 'contact':
      return block.info;
    case 'columns':
      return block.columns;
    case 'nav':
      return block.items;
    case 'footer':
      return block.links;
    case 'pagecta':
      return block.buttons;
    default:
      return null;
  }
}

export function itemOp(home: Block[], blockId: string, itemId: string, op: 'duplicate' | 'remove' | 'up' | 'down'): string | null {
  const loc = locate(home, blockId);
  const items = loc ? itemsOf(loc.block) : null;
  if (!loc || !items) return null;
  const i = items.findIndex((x) => x.id === itemId);
  if (i < 0) return null;
  if (op === 'remove') {
    items.splice(i, 1);
    return itemId;
  }
  if (op === 'up' || op === 'down') {
    const to = op === 'up' ? i - 1 : i + 1;
    if (to < 0 || to >= items.length) return null;
    const [x] = items.splice(i, 1);
    items.splice(to, 0, x!);
    return itemId;
  }
  const copy = structuredClone(items[i]!) as { id: string; key?: string; blocks?: Block[] };
  const taken = takenIds(home);
  copy.id = newId('item', taken);
  delete copy.key;
  if (copy.blocks) copy.blocks = copy.blocks.map((b) => freshIds(b, taken));
  items.splice(i + 1, 0, copy);
  return copy.id;
}

/* ── Liste ve tablo ─────────────────────────────────────── */

/** `items.2.items.0` yolundaki liste öğesini ve onu taşıyan diziyi verir. */
function listSlot(block: Extract<Block, { type: 'list' }>, path: string): { list: ListItem[]; index: number } | null {
  const parts = path.split('.');
  if (parts[0] !== 'items') return null;
  let list = block.items;
  let index = Number(parts[1]);
  for (let p = 2; p + 1 < parts.length; p += 2) {
    const next = list[index]?.items;
    if (!next || parts[p] !== 'items') return null;
    list = next;
    index = Number(parts[p + 1]);
  }
  return Number.isInteger(index) && list[index] ? { list, index } : null;
}

/** Liste öğesinin ardına yeni öğe; yeni öğenin metin yolunu döndürür. */
export function splitListItem(block: Extract<Block, { type: 'list' }>, itemPath: string, before: string, after: string): string | null {
  const slot = listSlot(block, itemPath);
  if (!slot) return null;
  slot.list[slot.index]!.text = { ...slot.list[slot.index]!.text, tr: before };
  slot.list.splice(slot.index + 1, 0, { text: { tr: after } });
  return itemPath.replace(/\d+$/, String(slot.index + 1));
}

export function removeListItem(block: Extract<Block, { type: 'list' }>, itemPath: string): boolean {
  const slot = listSlot(block, itemPath);
  if (!slot || (slot.list === block.items && block.items.length === 1)) return false;
  slot.list.splice(slot.index, 1);
  return true;
}

export function tableOp(block: Extract<Block, { type: 'table' }>, op: 'addRow' | 'addCol' | 'removeRow' | 'removeCol', at: number): void {
  const width = block.rows[0]?.length ?? 1;
  if (op === 'addRow') block.rows.splice(at + 1, 0, Array.from({ length: width }, () => ({ tr: '' })));
  if (op === 'addCol') block.rows.forEach((r) => r.splice(at + 1, 0, { tr: '' }));
  if (op === 'removeRow' && block.rows.length > 1) block.rows.splice(at, 1);
  if (op === 'removeCol' && width > 1) block.rows.forEach((r) => r.splice(at, 1));
}
